'use client'

import { useTranslations } from 'next-intl'
import type { SubstitutionPref } from '@/lib/online-orders/substitutionPrefs'
import { SubstitutionPrefSelector } from './SubstitutionPrefSelector'

type Props = {
  value: SubstitutionPref
  onChange: (p: SubstitutionPref) => void
  /** El cliente ya marcó «No quiero recibir SMS» en «Tus datos» (volvió con
   *  «Cambiar»): «Preguntarme primero» sigue elegible, pero se le dice qué falta. */
  smsDeclined: boolean
  total: number
  accentColor: string
  onContinue: () => void
  onBack: () => void
}

/**
 * «Si algo no está disponible» como paso propio, justo después del carrito
 * (diseño aprobado por el owner 2026-11-03). Antes vivía al final de «Tus
 * datos», debajo del aviso de SMS, y no se entendía ahí.
 *
 * Todavía no hay teléfono (se pide en el paso siguiente), así que «Preguntarme
 * primero» NO se deshabilita: se avisa que se le escribe a ese número. Si
 * después rechaza los SMS, CustomerStep la baja a «similar» con aviso, y el
 * servidor degrada igual (`contact_needs_sms`, lib/online-orders/substitutionPrefs).
 * Pago crea la sesión de cobro al montarse con la preferencia que haya, así que
 * elegirla ANTES de Pago es lo que la hace llegar al pedido.
 */
export function SubstitutionsStep({ value, onChange, smsDeclined, total, accentColor, onContinue, onBack }: Props) {
  const t = useTranslations('public.onlineOrder.checkout')
  const tCommon = useTranslations('common')

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center gap-2 px-5 py-4 border-b border-gray-100">
        <button
          type="button"
          onClick={onBack}
          className="text-sm font-medium text-gray-600 hover:text-gray-900"
        >
          ← {t('back')}
        </button>
        <h2 className="flex-1 text-center text-xl font-bold text-gray-900 -ml-12">
          {t('substitution.title')}
        </h2>
      </div>

      {/* Body */}
      <div className="flex-1 min-h-0 overflow-y-auto px-5 py-4">
        <p className="text-sm text-gray-600 mb-4">{t('substitution.stepIntro')}</p>
        <SubstitutionPrefSelector
          value={value}
          onChange={onChange}
          // Acá la opción nunca se apaga: el teléfono se pide en el paso siguiente.
          contactAllowed
          accentColor={accentColor}
          compact
        />
        {value === 'contact' && (
          <p className="text-xs text-gray-500 mt-3" data-testid="substitution-contact-note">
            {smsDeclined ? t('substitution.contactNeedsSms') : t('substitution.stepContactNote')}
          </p>
        )}
      </div>

      {/* Footer */}
      <div
        className="border-t border-gray-100 bg-white px-5 pt-4"
        style={{ paddingBottom: 'max(1rem, env(safe-area-inset-bottom))' }}
      >
        <div className="flex items-center justify-between text-sm mb-3">
          <span className="text-gray-600">{tCommon('total')}</span>
          <span className="font-bold text-gray-900">${total.toFixed(2)}</span>
        </div>
        <button
          type="button"
          onClick={onContinue}
          className="w-full rounded-full py-4 text-white text-base font-bold transition hover:opacity-95 active:scale-[0.99]"
          style={{ backgroundColor: accentColor }}
        >
          {t('substitution.stepContinue')} →
        </button>
      </div>
    </div>
  )
}
